import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  useSubscription,
  useSubscriptionContext
} from '@sopranodesign/pulse-subscribe';

import { appendServerMessages, appendMessage, appendLocationMessage } from '../utilities';
import useDelayedBot, {
  defaultOptions as defaultDelayedBotOptions
} from '../hooks/useDelayedBot';
import { useAuth } from '../hooks';
import { DeliveryContext } from '../Contexts';

// How often to resend the `subscribe` payload while the socket stays
// connected. This refreshes any server-side channel/session registration
// (e.g. the mapping used to route Live Chat agent replies back to this
// widget) that may expire or be evicted from a distributed cache without
// the underlying socket connection ever dropping, which would otherwise
// go unnoticed since normal bot messaging can continue to work.
const RESUBSCRIBE_INTERVAL_MS = 4 * 60 * 1000;

const DeliveryProvider = ({
  children,
  demo = false,
  client_id,
  base_url,
  setting = null,
  channelDriverName = 'Converse',
  delayedBotOptions = defaultDelayedBotOptions,
  browserUrl = null,
  variables
}) => {
  const [subscribed, setSubscribed] = useState(false);

  const { setMessages, setQueuedMessages } = useDelayedBot(delayedBotOptions);
  const { connected } = useSubscriptionContext();
  const { token } = useAuth();
  const channelRef = useRef();

  const getChannelParams = useCallback(() => {
    return {
      client_id,
      token,
      url: browserUrl,
      variables,
      demo
    };
  }, [client_id, token, browserUrl, variables, demo]);

  const handleSubscribe = useCallback(data => {
    channelRef.current = data.channel;

    setSubscribed(true);
  }, []);

  const handleMessage = useCallback(
    messages => setQueuedMessages(appendServerMessages(messages)),
    [setQueuedMessages]
  );

  const handleUnsubscribe = useCallback(() => null, []);

  const subscribe = useSubscription('subscribe', handleSubscribe);
  const unsubscribe = useSubscription('unsubscribe', handleUnsubscribe);
  const message = useSubscription('message', handleMessage);

  const sendMessage = (newMessage, postback = null) => {
    // Add the messages to the widget, as well as a typing indicator.
    setMessages(appendMessage(newMessage));
    message({
      ...getChannelParams(),
      message: newMessage,
      postback: postback
    });
  };

  const sendLocationMessage = (action = null, latitude = null, longitude = null) => {
    const content = { text: action, latitude, longitude };

    setMessages(
      appendLocationMessage({
        type: 'location',
        content
      })
    );

    message({
      ...getChannelParams(),
      message: JSON.stringify(content)
    });
  };

  /**
   * Subscribe on mount, and whenever the socket (re)connects.
   */
  useEffect(() => {
    subscribe({
      ...getChannelParams(),
      channel_driver_name: channelDriverName
    });

    return () => {
      unsubscribe({ channel: channelRef.current });
    };
  }, [subscribe, unsubscribe, getChannelParams, connected, channelDriverName]);

  /**
   * Periodically resend the subscribe payload while connected, even if the
   * socket never disconnects. This keeps any server-side channel/session
   * registration fresh so that out-of-band deliveries (such as a Live Chat
   * agent claiming a ticket and replying) can still find this widget after
   * it has been sitting idle, without requiring a manual widget/token reset.
   */
  useEffect(() => {
    if (!connected) {
      return undefined;
    }

    const interval = setInterval(() => {
      subscribe({
        ...getChannelParams(),
        channel_driver_name: channelDriverName
      });
    }, RESUBSCRIBE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [subscribe, getChannelParams, channelDriverName, connected]);

  const context = {
    // API
    base_url,
    // Browser
    browserUrl,
    variables,
    // Client
    client_id,
    // Channel details
    subscribed,
    setting,
    hasChannel: true,
    getChannelParams,
    channelDriverName,
    // Send events.
    sendMessage,
    subscribe,
    sendLocationMessage
  };

  return (
    <DeliveryContext.Provider value={context}>
      {children}
    </DeliveryContext.Provider>
  );
};

DeliveryProvider.propTypes = {
  channelDriverName: PropTypes.string.isRequired,
  client_id: PropTypes.string.isRequired,
  base_url: PropTypes.string.isRequired,
  channelKey: PropTypes.string,
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  setting: PropTypes.number,
  seenBannerKey: PropTypes.string,
  demo: PropTypes.bool,
  delayedBotOptions: PropTypes.shape({
    messageDelay: PropTypes.number,
    maxTypingTime: PropTypes.number
  }),
  browserUrl: PropTypes.string
};

export default DeliveryProvider;
